import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TrendingUp, TrendingDown, FolderKanban, FileText, Activity, Users, Sparkles, Download } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, RadialBarChart, RadialBar,
  PieChart, Pie, Cell, XAxis, Tooltip,
} from "recharts";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

// ---- Placeholder sample data (until real metrics are defined) ----
const rand = (n, lo, hi) => Array.from({ length: n }, (_, i) => ({ x: i, v: Math.round(lo + Math.random() * (hi - lo)) }));
const SAMPLE = {
  totalUsers: rand(28, 30, 95),
  activeGauge: [{ name: "active", value: 78, fill: "#2563eb" }],
  sales: 75,
  revenue: rand(20, 8, 60),
  newClients: rand(20, 4, 40),
  subs: rand(24, 10, 70),
};

const PROJ_TONE = {
  active: "bg-blue-50 text-blue-700 border-blue-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  done: "bg-green-50 text-green-700 border-green-200",
};
const QUOTE_TONE = {
  won: "bg-green-50 text-green-700", lost: "bg-rose-50 text-rose-700",
  draft: "bg-slate-100 text-slate-600", submitted: "bg-blue-50 text-blue-700",
  approved: "bg-emerald-50 text-emerald-700", sent: "bg-indigo-50 text-indigo-700",
  expired: "bg-amber-50 text-amber-700",
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");
const money = (n, c) => `${c === "USD" ? "US$" : "$"}${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Spark = ({ data, color, type = "area" }) => (
  <ResponsiveContainer width="100%" height={48}>
    {type === "bar" ? (
      <BarChart data={data}><Bar dataKey="v" fill={color} radius={[2, 2, 0, 0]} /></BarChart>
    ) : (
      <AreaChart data={data} margin={{ top: 2, bottom: 0, left: 0, right: 0 }}>
        <defs><linearGradient id={`g-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} /><stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient></defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#g-${color})`} />
      </AreaChart>
    )}
  </ResponsiveContainer>
);

function Delta({ value, suffix = "%", invert = false }) {
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${good ? "text-green-600" : "text-rose-500"}`}>
      {Math.abs(value)}{suffix} <Icon className="h-3 w-3" />
    </span>
  );
}

function KpiCard({ label, value, delta, period = "Last 7 days", children, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
        <span className="text-[11px] text-slate-400">{period}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <span className="text-2xl font-bold text-slate-900">{value}</span>
        {delta != null && <Delta value={delta} />}
      </div>
      <div className="mt-2 -mx-1">{children}</div>
    </Card>
  );
}

function PlaceholderBoard() {
  return (
    <div className="space-y-3" data-testid="qbr-placeholder-board">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-semibold text-slate-700">Performance snapshot</h2>
        <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-[10px] text-indigo-600" data-testid="qbr-sample-badge">
          Sample data · placeholder
        </Badge>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Hero */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 p-5 text-white lg:col-span-1">
          <p className="text-lg font-bold">Welcome back</p>
          <p className="mt-1 text-sm text-slate-300">Your quarterly business review at a glance.</p>
          <div className="mt-6 flex gap-8">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Today's sales</p>
              <p className="text-xl font-bold">6,782 <span className="text-xs text-green-400">+7%</span></p>
              <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-white/10"><div className="h-full w-3/4 rounded-full bg-green-400" /></div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Growth rate</p>
              <p className="text-xl font-bold">78.4% <span className="text-xs text-rose-400">-1%</span></p>
              <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-white/10"><div className="h-full w-2/3 rounded-full bg-rose-400" /></div>
            </div>
          </div>
        </Card>
        {/* Total users */}
        <Card className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total users</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">75,782 <Delta value={2} /></p>
          <p className="text-xs text-slate-400">24,635 users increased from last month</p>
          <div className="mt-3"><Spark data={SAMPLE.totalUsers} color="#2563eb" /></div>
        </Card>
        {/* Active gauge */}
        <Card className="flex flex-col p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Active users</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">25,782 <Delta value={-1} /></p>
          <div className="relative mx-auto mt-1 h-[110px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={SAMPLE.activeGauge} startAngle={210} endAngle={-30}>
                <RadialBar background dataKey="value" cornerRadius={8} />
              </RadialBarChart>
            </ResponsiveContainer>
            <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-slate-800">78%</span>
          </div>
        </Card>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Sales" value="75%" testid="qbr-kpi-sales">
          <div className="px-1"><Progress value={75} className="h-2" /><p className="mt-1.5 text-xs text-slate-400">Conversion rate <span className="font-semibold text-green-600">7% ↑</span></p></div>
        </KpiCard>
        <KpiCard label="Revenue" value="$4,300" delta={8} testid="qbr-kpi-revenue"><Spark data={SAMPLE.revenue} color="#2563eb" /></KpiCard>
        <KpiCard label="New clients" value="6,782" delta={0} testid="qbr-kpi-clients"><Spark data={SAMPLE.newClients} color="#0ea5e9" /></KpiCard>
        <KpiCard label="Active subscriptions" value="2,986" delta={4} testid="qbr-kpi-subs"><Spark data={SAMPLE.subs} color="#6366f1" type="bar" /></KpiCard>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3">
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}><Icon className="h-4 w-4" /></span>
      <div>
        <p className="text-lg font-bold leading-none text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}

export default function QBR() {
  const { user, has } = useAuth();
  const isPartner = user?.base_role === "partner";
  const canExport = has("qbr.export");
  const [searchParams] = useSearchParams();
  const presetPartner = searchParams.get("partner") || "";
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState(presetPartner);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/qbr/partners").then((r) => {
      setPartners(r.data || []);
      if (!presetPartner && (r.data || []).length) setPartnerId(r.data[0].org_id);
    }).catch(() => setPartners([]));
  }, [presetPartner]);

  useEffect(() => {
    if (!partnerId) return;
    setLoading(true);
    const params = new URLSearchParams({ partner_org_id: partnerId });
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    api.get(`/qbr?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [partnerId, start, end]);

  const catRows = useMemo(() => Object.entries(data?.activity?.by_category || {}).sort((a, b) => b[1] - a[1]), [data]);
  const CAT_COLORS = ["#2563eb", "#16a34a", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#db2777"];

  const exportPdf = async () => {
    if (!partnerId && !isPartner) { toast.error("Select a partner first"); return; }
    const t = toast.loading("Generating QBR PDF…");
    try {
      const params = new URLSearchParams();
      if (partnerId) params.set("partner_org_id", partnerId);
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const res = await api.get(`/qbr/pdf?${params.toString()}`, { responseType: "blob" });
      downloadBlob(new Blob([res.data], { type: "application/pdf" }), `QBR-${(data?.partner?.name || "partner").replace(/[^a-z0-9]+/gi, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("QBR PDF downloaded", { id: t });
    } catch { toast.error("Failed to export QBR PDF", { id: t }); }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-1" data-testid="qbr-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Partner QBR</h1>
          <p className="mt-1 text-sm text-slate-500">Quarterly Business Review — projects, quotes &amp; activity.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {!isPartner && (
            <div>
              <Label className="text-xs text-slate-500">Partner</Label>
              <Select value={partnerId} onValueChange={setPartnerId}>
                <SelectTrigger className="mt-1 w-[230px]" data-testid="qbr-partner-select"><SelectValue placeholder="Select a partner" /></SelectTrigger>
                <SelectContent>
                  {partners.map((p) => <SelectItem key={p.org_id} value={p.org_id} data-testid={`qbr-partner-option-${p.org_id}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-[150px]" data-testid="qbr-start-date" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 w-[150px]" data-testid="qbr-end-date" />
          </div>
          {canExport && (
            <Button onClick={exportPdf} className="mt-1" data-testid="qbr-export-pdf" disabled={!partnerId}>
              <Download className="h-4 w-4" /> Export PDF
            </Button>
          )}
        </div>
      </div>

      <PlaceholderBoard />

      <div className="border-t border-slate-100 pt-5">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-700">
            {data?.partner?.name || (loading ? "Loading…" : "Partner")} summary
          </h2>
          {data?.partner?.partner_type && <Badge variant="outline" className="text-[10px]">{data.partner.partner_type}</Badge>}
          {data && (
            <span className="text-xs text-slate-400">
              · {data.customers_count} customers · {data.users_count} users
              {(start || end) ? ` · ${start || "…"} → ${end || "…"}` : " · all time"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Projects */}
          <Card className="p-5" data-testid="qbr-projects-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-700"><FolderKanban className="h-4 w-4 text-blue-600" /><h3 className="text-sm font-semibold">Projects</h3></div>
              <span className="text-2xl font-bold text-slate-900" data-testid="qbr-projects-total">{data?.projects?.total ?? 0}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <StatTile icon={FolderKanban} label="Active" value={data?.projects?.active ?? 0} tone="bg-blue-100 text-blue-700" />
              <StatTile icon={FolderKanban} label="Completed" value={data?.projects?.completed ?? 0} tone="bg-green-100 text-green-700" />
              <StatTile icon={TrendingUp} label="Avg progress" value={`${data?.projects?.avg_progress ?? 0}%`} tone="bg-indigo-100 text-indigo-700" />
            </div>
            <div className="mt-3 space-y-2">
              {(data?.projects?.rows || []).slice(0, 5).map((p) => (
                <div key={p.project_id} className="flex items-center justify-between gap-2 rounded-md border border-slate-100 p-2" data-testid={`qbr-project-row-${p.project_id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">{p.name}</p>
                    <p className="text-[11px] text-slate-400">{p.product_type || "—"} · go-live {fmtDate(p.target_go_live)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">{p.progress}%</span>
                    <Badge variant="outline" className={`text-[10px] ${PROJ_TONE[p.status] || ""}`}>{p.status}</Badge>
                  </div>
                </div>
              ))}
              {!loading && !(data?.projects?.rows || []).length && <p className="py-4 text-center text-sm text-slate-400">No projects in range.</p>}
            </div>
          </Card>

          {/* Quotes */}
          <Card className="p-5" data-testid="qbr-quotes-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-700"><FileText className="h-4 w-4 text-emerald-600" /><h3 className="text-sm font-semibold">Quotes</h3></div>
              <span className="text-2xl font-bold text-slate-900" data-testid="qbr-quotes-total">{data?.quotes?.count ?? 0}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <StatTile icon={FileText} label="Total value" value={money(data?.quotes?.total_value, "CAD")} tone="bg-emerald-100 text-emerald-700" />
              <StatTile icon={TrendingUp} label="Won" value={data?.quotes?.won_count ?? 0} sub={money(data?.quotes?.won_value, "CAD")} tone="bg-green-100 text-green-700" />
              <StatTile icon={Activity} label="Close ratio" value={`${data?.quotes?.closing_ratio ?? 0}%`} tone="bg-indigo-100 text-indigo-700" />
            </div>
            <div className="mt-3 space-y-2">
              {(data?.quotes?.rows || []).slice(0, 5).map((q) => (
                <div key={q.quote_id} className="flex items-center justify-between gap-2 rounded-md border border-slate-100 p-2" data-testid={`qbr-quote-row-${q.quote_id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">#{q.number} {q.name}</p>
                    <p className="text-[11px] text-slate-400">{q.company || "—"} · {fmtDate(q.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600">{money(q.total, q.currency)}</span>
                    <Badge variant="outline" className={`text-[10px] ${QUOTE_TONE[q.status] || ""}`}>{q.status}</Badge>
                  </div>
                </div>
              ))}
              {!loading && !(data?.quotes?.rows || []).length && <p className="py-4 text-center text-sm text-slate-400">No quotes in range.</p>}
            </div>
          </Card>

          {/* Activity */}
          <Card className="p-5" data-testid="qbr-activity-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-700"><Activity className="h-4 w-4 text-violet-600" /><h3 className="text-sm font-semibold">Activity</h3></div>
              <span className="text-2xl font-bold text-slate-900" data-testid="qbr-activity-total">{data?.activity?.count ?? 0}</span>
            </div>
            {catRows.length > 0 && (
              <div className="mt-2 flex items-center gap-3">
                <div className="h-[90px] w-[90px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={catRows.map(([k, v]) => ({ name: k, value: v }))} dataKey="value" innerRadius={26} outerRadius={42} paddingAngle={2}>
                        {catRows.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1">
                  {catRows.slice(0, 5).map(([k, v], i) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: CAT_COLORS[i % CAT_COLORS.length] }} />{k}</span>
                      <span className="font-semibold text-slate-700">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-3 space-y-2">
              {(data?.activity?.rows || []).slice(0, 5).map((a, i) => (
                <div key={i} className="rounded-md border border-slate-100 p-2" data-testid={`qbr-activity-row-${i}`}>
                  <p className="truncate text-sm font-medium text-slate-800">{a.action}</p>
                  <p className="text-[11px] text-slate-400">{a.actor_name || "—"} · {fmtDate(a.created_at)}</p>
                </div>
              ))}
              {!loading && !(data?.activity?.rows || []).length && <p className="py-4 text-center text-sm text-slate-400">No activity in range.</p>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
